import { ICreatePassportDto, IEquipmentPassport, TCreateEquipmentDTO } from '@/pages/Equipment/equipment.types.ts'
import useStateWithChange from '@/shared/hooks/useStateWithChange.ts'
import { useCallback, useState } from 'react'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { IPassportGroup } from '@/pages/MDM/mdm.types.ts'
import useNestedArrayChangeCallbacks from '@/shared/hooks/useNestedArrayChangeCallbacks.ts'
import usePassportSchemas from '@/pages/MDM/hooks/usePassportSchemas.ts'
import mdmUtils from '@/pages/MDM/mdm.utils'

interface IProps {
  equipment: TCreateEquipmentDTO
  passport: ICreatePassportDto
}

type TTab = 'main' | 'passport' | 'connection-configuration'

/**
 * Контроллер формы оборудования. Используется вместе с компонентами EquipmentFormFields и EquipmentPassportValuesForm
 * */
const useEquipmentForm = (props: IProps) => {
  const passportSchemas = usePassportSchemas()
  const groupSelectCtrl = useTreeSelectCtrl<IPassportGroup['id']>(`/equipment/create-passport/`, null, 'regularState')

  const [draft, _, handleChange] = useStateWithChange(props.equipment)
  const [draftPassport, setDraftPassport] = useStateWithChange<ICreatePassportDto>(props.passport)
  const [selectedTab, setSelectedTab] = useState<TTab>('main')

  const { handleElementUpdate: handlePassportValueUpdate } = useNestedArrayChangeCallbacks({
    sourceObjectSetter: setDraftPassport,
    nestedArrayKey: 'values',
  })

  const handleGroupSelect = useCallback(
    (groupId: IPassportGroup['id']) => {
      const a = document.createElement('a')
      a.href = `#group_${groupId}`
      a.click()

      setSelectedTab('passport')
      groupSelectCtrl.handleSelect(groupId)

      a.remove()
    },
    [groupSelectCtrl.handleSelect]
  )

  const handleSchemaSelect = (id: IEquipmentPassport['id']) => {
    const schema = passportSchemas.find((s) => s.id === id)

    setDraftPassport((prev) => ({
      ...prev,
      passportSchemaId: id,
      values: schema.fields.map((f) => mdmUtils.generateCreatePassportValueDTO(f.id)),
    }))
  }

  const handleTabSelect = (tab: TTab) => {
    setSelectedTab(tab)
    if (tab !== 'passport') {
      groupSelectCtrl.handleSelect(null)
    }
  }

  return {
    equipment: draft,
    handleEquipmentChange: handleChange,
    handlePassportSchemaSelect: handleSchemaSelect,
    handlePassportGroupSelect: handleGroupSelect,
    passport: draftPassport,
    handlePassportValueUpdate: handlePassportValueUpdate,
    selectedTab,
    handleTabSelect,
    groupSelectCtrl,
  }
}

export default useEquipmentForm
